import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { 
  ChevronLeft, 
  ChevronRight, 
  Search, 
  Clear, 
  MoreVert, 
  Download as DownloadIcon,
  Delete,
  Refresh,
  Check
} from "@mui/icons-material";
import { memo, useEffect, useMemo, useState } from "react";
import { useClassroomActions } from "../../../../../hooks/useClassroomActions";
import { Assessment, Item } from "../../../../../models/assessment";
import { useSnackbar } from "../../../../../components/snackbar";
import DeleteResponsesDialog from "../delete-responses-dialog";
import { useNavigate } from "react-router-dom";

const TruncatedText = ({ text, maxLength = 50 }: { text: string; maxLength?: number }) => {
  if (!text || text.length <= maxLength) {
    return <Typography variant="body2">{text || "-"}</Typography>;
  }

  return (
    <Tooltip title={text} placement="top">
      <Typography 
        variant="body2" 
        sx={{ 
          cursor: "pointer",
          "&:hover": { textDecoration: "underline" }
        }}
      >
        {text.substring(0, maxLength)}...
      </Typography>
    </Tooltip>
  );
};

const extractTextFromTipTap = (node: any): string => {
  if (!node) return "";
  if (node.type === "text") return node.text || "";
  if (Array.isArray(node.content)) return node.content.map(extractTextFromTipTap).join("");
  return extractTextFromTipTap(node.content);
};

interface ResponsesProps {
  assessment: Assessment;
}

function Responses({ assessment }: ResponsesProps) {
  const navigate = useNavigate();
  const { getAssessmentResponses, deleteAssessmentResponses } = useClassroomActions();
  
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [fetching, setFetching] = useState(false);
  
  const [deleting, setDeleting] = useState(false);

  const [deleteResponsesDialog, setDeleteResponsesDialog] = useState(false);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [searchTerm, setSearchTerm] = useState("");

  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  const [canLoadMore, setCanLoadMore] = useState(true);;

  const [currentPage, setCurrentPage] = useState(1);
 
  const itemsPerPage = 50;
  const open = Boolean(anchorEl);
  const [filteredResponses, setFilteredResponses] = useState<any[]>([]);

  const { showMessage } = useSnackbar();

  const totalPages = Math.ceil(filteredResponses.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedResponses = filteredResponses.slice(startIndex, startIndex + itemsPerPage);


  // const flattenedQuestions = useMemo(() => {
  //   if (!assessment.questions) return [];
  //   const result: { id: string; title: string, type: number }[] = [];
  //   assessment.questions.forEach((section, sectionIndex) => {
  //     section.forEach((q: Item) => {
  //       if (q.type !== 0) {
  //         result.push({
  //           id: q.id,
  //           title: `Section ${sectionIndex + 1}: ${extractTextFromTipTap(q.title)}`,
  //           type: q.type
  //         });
  //       }
  //     });
  //   });
  //   return result;
  // }, [assessment.questions]);

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedRows(paginatedResponses.map((row) => row.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleRowClick = (submissionId: number) => {
    const selectedIndex = selectedRows.indexOf(submissionId);
    let newSelected: number[] = [];

    if (selectedIndex === -1) {
      newSelected = [...selectedRows, submissionId];
    } else if (selectedIndex === 0) {
      newSelected = selectedRows.slice(1);
    } else if (selectedIndex === selectedRows.length - 1) {
      newSelected = selectedRows.slice(0, -1);
    } else if (selectedIndex > 0) {
      newSelected = [
        ...selectedRows.slice(0, selectedIndex),
        ...selectedRows.slice(selectedIndex + 1),
      ];
    }

    setSelectedRows(newSelected);
  };

  const isRowSelected = (submissionId: number): boolean => {
    return selectedRows.includes(submissionId);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    if (assessment.responses) {
      setFilteredResponses(assessment.responses);
    }
    setCurrentPage(1);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      const filtered = assessment.responses?.filter((submission: any) => {
        const searchLower = searchTerm.toLowerCase();
        return (
          submission.name?.toLowerCase().includes(searchLower) ||
          submission.reg_number?.toLowerCase().includes(searchLower)
        );
      }) ?? [];

      setFilteredResponses(filtered);
    }
  };

  const markingCompleted = () => {
    if (!assessment.responses) return true;

    for (let i = 0; i < Math.min(50, assessment.responses.length); i++) {
      const response = assessment.responses[i];

      if (response.submitted_at && !response.score) {
        return false;
      }
    }

    return true;
  }

  const handleDownloadCSV = () => {
    if (!assessment.responses) return;

    const baseHeaders = ['Name', 'Reg Number'];
    
    if (assessment.auto_marker) {
      baseHeaders.push('Score');
      // baseHeaders.push('Remarks')
    };

    baseHeaders.push('Started At');
    baseHeaders.push('Submitted At');

    // const questionHeaders = flattenedQuestions.map(q => q.title);
    // const headers = [...baseHeaders, ...questionHeaders];

    const rows = assessment.responses.map((submission: any) => {
      const row: string[] = [
        `"${submission.name || 'N/A'}"`,
        `"${submission.reg_number || 'N/A'}"`
      ];

      if (assessment.auto_marker) {
        row.push(`"'${submission.score ?? '-'}"`);
        // row.push(`"${JSON.stringify(submission.remarks) ?? '-'}"`);
      }

      row.push(`"${new Date(submission.started_at).toLocaleString() ?? "-"}"`);
      row.push(`"${new Date(submission.submitted_at).toLocaleString() ?? "-"}"`);

      // for (const q of flattenedQuestions) {
      //   const answer = submission.answers?.[q.id];
      //   row.push(`"${answer || '-'}"`);
      // }

      return row.join(',');
    });

    // swtiched to base headers from headers
    const csvContent = [baseHeaders.join(','), ...rows].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `elms_responses_${assessment.title || 'assessment'}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    handleMenuClose();
  };

  const handleDeleteSelected = async () => {
    setDeleteResponsesDialog(false);
    setDeleting(true);
    await deleteAssessmentResponses(assessment.classroom_id, assessment.id, selectedRows);
    setSelectedRows([]);
    setDeleting(false);
  };

  const fetchResponses = async (reset: boolean = false) => {
    if (!reset && (!canLoadMore || refreshing || fetching)) return;

    if (reset) {
      setRefreshing(true);
    } else {
      setFetching(true);
    }

    const newLength = await getAssessmentResponses(
      assessment.classroom_id, 
      assessment.id,
      reset
    ) || 0;

    setCanLoadMore(newLength < 50 ? false : true);

    if (reset) {
      setRefreshing(false);
      setCurrentPage(1);
    } else {
      setFetching(false);
      setCurrentPage(totalPages + 1);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      if (assessment.responses) return;
      
      setLoading(true);
      await fetchResponses();
      setLoading(false);
    };

    loadData();
  }, []);

  useEffect(() => {
    if (assessment.responses) {
      // const sorted = [...assessment.responses].sort((a, b) => {
      //   const nameA = a.name?.toLowerCase() || "";
      //   const nameB = b.name?.toLowerCase() || "";
      //   return nameA.localeCompare(nameB);
      // });

      setFilteredResponses(assessment.responses);
    }
  }, [assessment.responses]);

  if (!assessment.responses || loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', justifyContent: 'center' }}>
        <CircularProgress/>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        minHeight: '100vh'
      }}
    >

      <Paper
        elevation={1}
        sx={{ 
          borderRadius: 2, 
          border: '1px solid',
          borderColor: 'grey.300',
          overflow: 'hidden'
        }}
      >
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            padding: 1,
            gap: 2
          }}
        >
          <TextField
            placeholder="Search by name or registration number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={handleKeyPress}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: 'grey.400', fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClearSearch}
                    edge="end"
                    size="small"
                    sx={{
                      color: 'grey.400',
                      '&:hover': {
                        color: 'grey.600',
                        bgcolor: 'transparent'
                      }
                    }}
                  >
                    <Clear sx={{ fontSize: 18 }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              flexGrow: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                bgcolor: 'grey.100',
                height: '40px',
                '& fieldset': {
                  borderColor: 'grey.300'
                },
                '&:hover fieldset': {
                  borderColor: 'grey.400'
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main'
                }
              }
            }}
          />

          <Stack direction="row" spacing={1}>
            {selectedRows.length > 0 && (
              <Button
                variant="contained"
                color="error"
                startIcon={<Delete />}
                onClick={() => setDeleteResponsesDialog(true)}
                disabled={deleting}
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  borderRadius: 1
                }}
              >
                {deleting ? "Deleting..." : `Delete (${selectedRows.length})`}
              </Button>
            )}

            {assessment.auto_marker && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                {markingCompleted() ? (
                  <Tooltip title="Marking Completed">
                    <Check 
                      sx={{
                        color: '#2bc71a'
                      }}
                    />
                  </Tooltip>
                ): (
                  <Tooltip title="Marking In Progress">
                    <CircularProgress size={14} sx={{ color: "#f7a33b" }}/>
                  </Tooltip>
                )}
              </Box>
            )}
            
            <IconButton
              onClick={() => fetchResponses(true)}
            >
              {refreshing ? <CircularProgress color="inherit" size={20}/> : <Refresh sx={{ fontSize: 20 }}/>}
            </IconButton>
            <IconButton
              onClick={handleMenuClick}
              sx={{ 
                color: 'grey.600',
                '&:hover': {
                  bgcolor: 'grey.100'
                }
              }}
            >
              <MoreVert sx={{ fontSize: 20 }} />
            </IconButton>
            
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem onClick={handleDownloadCSV}>
                <ListItemIcon>
                  <DownloadIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Export to CSV</ListItemText>
              </MenuItem>
            </Menu>
          </Stack>
        </Box>

        <TableContainer>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedRows.length > 0 && selectedRows.length < paginatedResponses.length}
                    checked={paginatedResponses.length > 0 && selectedRows.length === paginatedResponses.length}
                    onChange={handleSelectAllClick}
                    size="small"
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: "grey.700", minWidth: 150 }}>
                  Name
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: "grey.700", minWidth: 120 }}>
                  Reg Number
                </TableCell>
                {assessment.auto_marker && (
                  <>
                    <TableCell sx={{ fontWeight: 600, color: "grey.700", minWidth: 80 }}>
                      Score
                    </TableCell>

                    {/* <TableCell sx={{ fontWeight: 600, color: "grey.700", minWidth: 80 }}>
                      Remarks
                    </TableCell> */}
                  </>
                )}
                 <TableCell sx={{ fontWeight: 600, color: "grey.700", minWidth: 140 }}>
                  Started At
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: "grey.700", minWidth: 140 }}>
                  Submitted At
                </TableCell>
                {/* {flattenedQuestions.map((q) => (
                  <TableCell 
                    key={q.id} 
                    sx={{ 
                      fontWeight: 600, 
                      color: "grey.700", 
                      minWidth: 200,
                      maxWidth: 300 
                    }}
                  >
                    <Tooltip title={q.title}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {q.title.length > 40 ? `${q.title.substring(0, 40)}...` : q.title}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                ))} */}
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedResponses.map((submission: any) => {
                const isSelected = isRowSelected(submission.id);
                return (
                  <TableRow 
                    key={submission.id}
                    sx={{
                      "&:hover": { 
                        bgcolor: "grey.50",
                        cursor: "pointer"
                      },
                      bgcolor: isSelected ? 'primary.50' : 'inherit'
                    }}
                    onClick={() => {
                      window.open(`/classroom/${assessment.classroom_id}/assessments/${assessment.id}/submissions/${submission.id}`, '_blank');
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => handleRowClick(submission.id)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 150 }}>
                      <Typography 
                        variant="body2" 
                        fontWeight={500}
                        sx={{ color: 'grey.900', mb: 0.5 }}
                      >
                        {submission.name || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 120 }}>
                      <Typography variant="body2" sx={{ color: 'grey.800' }}>
                        {submission.reg_number || 'N/A'}
                      </Typography>
                    </TableCell>
                    {assessment.auto_marker && (
                      <>
                        <TableCell sx={{ minWidth: 80 }}>
                          <Typography variant="body2" sx={{ color: 'grey.800' }}>
                            {submission.score || '-'}
                          </Typography>
                        </TableCell>

                        {/* <TableCell sx={{ minWidth: 80 }}>
                          <Typography variant="body2" sx={{ color: 'grey.800' }}>
                            {submission.remarks ? (
                              <TruncatedText text={JSON.stringify(submission.remarks)} maxLength={50}/>
                            ) : (
                              '-'
                            )}
                          </Typography>
                        </TableCell> */}
                      </>
                    )}
                    <TableCell sx={{ minWidth: 140 }}>
                      <Typography variant="body2" sx={{ color: 'grey.800' }}>
                        {submission.started_at ? new Date(submission.started_at).toLocaleString() : "-"}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ minWidth: 140 }}>
                      <Typography variant="body2" sx={{ color: 'grey.800' }}>
                        {submission.submitted_at ? new Date(submission.submitted_at).toLocaleString() : "-"}
                      </Typography>
                    </TableCell>
                    {/* {flattenedQuestions.map((q) => (
                      <TableCell 
                        key={q.id} 
                        sx={{ 
                          minWidth: 200,
                          maxWidth: 300 
                        }}
                      >
                        {q.type === 5 ? (
                          <a 
                            href={submission.answers[q.id]} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ color: '#1976d2', textDecoration: 'none' }}
                          >
                            Download file
                          </a>
                        ) : (
                          <TruncatedText text={submission.answers[q.id]} maxLength={50} />
                        )}
                      </TableCell>
                    ))} */}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: 1,
            backgroundColor: 'grey.100',
            gap: 3
          }}
        >

          <Typography
            variant="body2"
              sx={{
                cursor: !canLoadMore ? "not-allowed" : "pointer",
                color: !canLoadMore ? "text.disabled" : "primary.main",
                pointerEvents: !canLoadMore ? "none" : "auto",
                opacity: !canLoadMore ? 0.6 : 1,
              }}
                onClick={() => {
                  fetchResponses();
                }}
          >
            {fetching ? "loading..." : "Load more"}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Typography variant="body2" color="text.secondary">
              {((currentPage - 1) * itemsPerPage) + 1} – {Math.min(currentPage * itemsPerPage, filteredResponses.length)} of {filteredResponses.length}
            </Typography>

            <IconButton 
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
            >
              <ChevronLeft />
            </IconButton>

            <IconButton 
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
            >
              <ChevronRight />
            </IconButton>
          </Box>
        </Box>

      </Paper>

      <DeleteResponsesDialog
        open={deleteResponsesDialog}
        setOpen={setDeleteResponsesDialog}
        onProceed={handleDeleteSelected}
      />


    </Box>
  );
}

export default Responses;